import { Component, ReactNode } from 'react';
import { captureError } from '@shared/lib/observability';

interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * App-level fallback. Catches anything that escapes the per-section
 * SectionErrorBoundaries — most importantly a failed lazy chunk `import()`
 * (common on flaky KG links), which otherwise leaves a blank white screen.
 * Offers a full reload rather than a dead page.
 */
export class RootErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[RootErrorBoundary]', error, info);
    captureError(error, { boundary: 'root', componentStack: info.componentStack ?? null });
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        style={{
          fontFamily: 'system-ui, sans-serif',
          background: '#0d1117',
          color: 'var(--text)',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          textAlign: 'center',
        }}
      >
        <div style={{ maxWidth: 520 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>😕</div>
          <h1 style={{ fontSize: 20, margin: '0 0 12px', color: 'var(--accent)' }}>Something went wrong</h1>
          <p style={{ color: 'var(--dim)', lineHeight: 1.5, margin: '0 0 16px' }}>
            The app hit an unexpected error. Reloading usually fixes it.
          </p>
          <pre
            style={{
              color: 'var(--dim)',
              fontSize: 12,
              whiteSpace: 'pre-wrap',
              background: '#161b22',
              border: '1px solid #2a2d35',
              borderRadius: 8,
              padding: '10px 14px',
              marginBottom: 16,
              textAlign: 'left',
              maxHeight: 120,
              overflow: 'auto',
            }}
          >
            {String(this.state.error)}
          </pre>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 18px',
              borderRadius: 8,
              border: 'none',
              background: 'var(--accent)',
              color: '#fff',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
